import { type ButtonHTMLAttributes, useEffect, useMemo, useState } from "react";
import { Badge, Card, DataTable, EmptyState, PageHeader, type BadgeProps, type DataTableColumn } from "@pericles/framework-frontend";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary";
};

function Button({ variant = "primary", className, type = "button", ...props }: ButtonProps): JSX.Element {
  const classes = ["button", `button-${variant}`, className].filter(Boolean).join(" ");
  return <button type={type} className={classes} {...props} />;
}

import {
  downloadProjectBriefPdf,
  fetchProjectChecklist,
  generateProjectChecklist,
} from "../api/projectChecklistClient";
import type { ProjectHealthChecklistItem, ProjectHealthSnapshot, ProjectHealthStatus } from "../types/projectHealth";

import { InlineAlert } from "./InlineAlert";

interface ProjectChecklistPanelProps {
  projectId: string;
}

const HEALTH_STATUS_BADGES: Record<ProjectHealthStatus, { label: string; variant: BadgeProps["variant"] }> = {
  on_track: { label: "On track", variant: "success" },
  at_risk: { label: "At risk", variant: "warning" },
  off_track: { label: "Off track", variant: "danger" },
};

function renderStatusBadge(status?: ProjectHealthStatus): JSX.Element {
  if (!status || !(status in HEALTH_STATUS_BADGES)) {
    return <Badge label="Unknown" variant="default" />;
  }

  const badge = HEALTH_STATUS_BADGES[status];
  return <Badge label={badge.label} variant={badge.variant} />;
}

function formatDate(timestamp?: string): string | null {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

function useCategoryCoverage(items: ProjectHealthChecklistItem[]) {
  return useMemo(() => {
    const map = new Map<string, { total: number; healthy: number }>();

    items.forEach((item) => {
      const category = item.category?.trim() || "General";
      const current = map.get(category) ?? { total: 0, healthy: 0 };
      current.total += 1;
      if (item.status === "on_track") {
        current.healthy += 1;
      }
      map.set(category, current);
    });

    return Array.from(map.entries()).map(([category, stats]) => ({
      category,
      ...stats,
      coverage: stats.total ? Math.round((stats.healthy / stats.total) * 100) : 0,
    }));
  }, [items]);
}

export function ProjectChecklistPanel({ projectId }: ProjectChecklistPanelProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<ProjectHealthSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const loadChecklist = async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const existing = await fetchProjectChecklist(projectId);
      setSnapshot(existing);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load project checklist";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadChecklist();
  }, [projectId]);

  const handleGenerate = async () => {
    if (!projectId) return;
    setIsGenerating(true);
    setError(null);

    try {
      const generated = await generateProjectChecklist(projectId);
      setSnapshot(generated);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate project checklist";
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadBrief = async () => {
    if (!projectId) return;
    setDownloadError(null);

    try {
      const blob = await downloadProjectBriefPdf(projectId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `project-${projectId}-brief.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to download project brief";
      setDownloadError(message);
    }
  };

  const categoryCoverage = useCategoryCoverage(snapshot?.checklist ?? []);
  const uncoveredItems = useMemo(
    () => (snapshot?.checklist ?? []).filter((item) => item.status !== "on_track"),
    [snapshot?.checklist],
  );

  const columns = useMemo<DataTableColumn<ProjectHealthChecklistItem>[]>(
    () => [
      { id: "category", header: "Category", accessor: (row) => row.category ?? "General" },
      { id: "label", header: "Checklist item", accessor: (row) => row.label },
      { id: "status", header: "Status", accessor: (row) => renderStatusBadge(row.status), width: 160, align: "center" },
    ],
    [],
  );

  const updatedAt = formatDate(snapshot?.updatedAt);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Project coverage" />

      {error ? <InlineAlert tone="danger" title={error} /> : null}
      {downloadError ? <InlineAlert tone="warning" title={downloadError} /> : null}

      {isLoading ? (
        <Card>
          <div className="p-4 text-sm text-slate-600">Loading project checklist...</div>
        </Card>
      ) : !snapshot ? (
        <Card>
          <EmptyState
            title="No checklist yet"
            description="Generate a project-level health summary and checklist with AI."
            action={
              <Button onClick={handleGenerate} disabled={isGenerating} variant="primary">
                {isGenerating ? "Generating..." : "Generate with AI"}
              </Button>
            }
          />
        </Card>
          ) : (
            <div className="flex flex-col gap-4">
              <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="text-lg font-semibold text-slate-900">Health status</div>
                {renderStatusBadge(snapshot.summary?.status)}
                {updatedAt ? <span className="text-sm text-slate-600">Updated {updatedAt}</span> : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="button button-secondary button-sm"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Regenerating..." : "Regenerate with AI"}
                </button>
                <button
                  type="button"
                  className="button button-primary button-sm"
                  onClick={handleDownloadBrief}
                  disabled={!snapshot.briefPdfAvailable}
                >
                  Download project brief
                </button>
              </div>
            </div>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Health summary">
              <div className="flex flex-col gap-2">
                <div className="text-sm text-slate-800">{snapshot.summary?.overview}</div>

                {snapshot.summary?.keyRisks?.length ? (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Key risks</div>
                    <ul className="list-disc pl-5 text-sm text-slate-700">
                      {snapshot.summary.keyRisks.map((risk) => (
                        <li key={risk}>{risk}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {snapshot.summary?.recommendations?.length ? (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Recommendations / assumptions</div>
                    <ul className="list-disc pl-5 text-sm text-slate-700">
                      {snapshot.summary.recommendations.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </Card>

            <Card title="Category coverage">
              {categoryCoverage.length === 0 ? (
                <div className="text-sm text-slate-600">No checklist items available.</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {categoryCoverage.map((category) => (
                    <div key={category.category} className="flex items-center justify-between gap-3 rounded border p-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{category.category}</div>
                        <div className="text-xs text-slate-600">
                          {category.healthy} of {category.total} on track
                        </div>
                      </div>
                      <Badge
                        label={`${category.coverage}% covered`}
                        variant={category.coverage >= 80 ? "success" : category.coverage >= 50 ? "warning" : "danger"}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card title="Checklist items non couverts">
            {uncoveredItems.length === 0 ? (
              <div className="text-sm text-slate-600">Tous les items sont couverts.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {uncoveredItems.map((item) => (
                  <div key={item.id} className="flex flex-wrap items-start justify-between gap-3 rounded border p-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{item.label}</div>
                      <div className="text-xs text-slate-600">Catégorie : {item.category ?? "General"}</div>
                      {item.notes ? <div className="text-xs text-slate-600">Notes : {item.notes}</div> : null}
                    </div>

                    <div className="flex items-center gap-2">{renderStatusBadge(item.status)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Checklist">
            <DataTable<ProjectHealthChecklistItem>
              columns={columns}
              data={snapshot.checklist}
              emptyState={<EmptyState title="No checklist items" description="No checklist items were generated yet." />}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
